import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { TierBadge } from "@/components/StatusBadge";
import { AlertTriangle, TrendingUp, TrendingDown, Minus, Search } from "lucide-react";

const TIER_OPTIONS = ["all", "Unrated", "Below Threshold", "Bronze", "Silver", "Gold", "Platinum"];
const SPEC_OPTIONS = [
  "all",
  "AI testing",
  "LLM evaluation",
  "automation",
  "accessibility",
  "fintech",
  "banking",
  "healthcare",
  "ecommerce",
  "security",
  "performance",
];

function TrendCell({ delta }) {
  if (delta == null) return <span className="text-slate-400 text-xs">—</span>;
  if (delta > 0) return <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium"><TrendingUp className="w-3.5 h-3.5"/>+{delta}</span>;
  if (delta < 0) return <span className="inline-flex items-center gap-1 text-red-700 text-xs font-medium"><TrendingDown className="w-3.5 h-3.5"/>{delta}</span>;
  return <span className="inline-flex items-center gap-1 text-slate-500 text-xs"><Minus className="w-3.5 h-3.5"/>0</span>;
}

function SortableTh({ label, k, sort, onSort, align = "left" }) {
  const active = sort.key === k;
  return (
    <th
      onClick={() => onSort(k)}
      className={`cursor-pointer select-none ${align === "right" ? "text-right" : ""}`}
      data-testid={`th-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <span className="text-slate-500">{sort.dir === "asc" ? "▲" : "▼"}</span>}
      </span>
    </th>
  );
}

export default function TesterScores() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");
  const [spec, setSpec] = useState("all");
  const [range, setRange] = useState({ min: "", max: "" });
  const [sort, setSort] = useState({ key: "score", dir: "desc" });

  const load = async () => {
    setBusy(true);
    try {
      const params = {};
      if (tier !== "all") params.tier = tier;
      if (spec !== "all") params.specialization = spec;
      if (range.min !== "") params.min_score = Number(range.min);
      if (range.max !== "") params.max_score = Number(range.max);
      const { data } = await api.get("/admin/tester-scores", { params });
      setRows(data.items || []);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, [tier, spec]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = rows;
    if (term) {
      out = out.filter(r =>
        (r.name || "").toLowerCase().includes(term)
        || (r.email || "").toLowerCase().includes(term)
      );
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    const sortKey = sort.key;
    return [...out].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string") return av.localeCompare(bv) * dir;
      return (av - bv) * dir;
    });
  }, [rows, q, sort]);

  const onSort = (k) => setSort(prev => (
    prev.key === k ? { key: k, dir: prev.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "desc" }
  ));

  return (
    <AppLayout title="Tester Scores" subtitle="Rankings, tiers, trends & anomaly flags">
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
          <Input
            placeholder="Search by name or email"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="pl-9"
            autoCapitalize="none"
            autoCorrect="off"
            data-testid="input-search-testers"
          />
        </div>
        <Select value={tier} onValueChange={setTier}>
          <SelectTrigger className="w-40" data-testid="select-tier"><SelectValue placeholder="Tier"/></SelectTrigger>
          <SelectContent>
            {TIER_OPTIONS.map(t => <SelectItem key={t} value={t}>{t === "all" ? "All tiers" : t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={spec} onValueChange={setSpec}>
          <SelectTrigger className="w-52" data-testid="select-specialization"><SelectValue placeholder="Specialization"/></SelectTrigger>
          <SelectContent>
            {SPEC_OPTIONS.map(t => <SelectItem key={t} value={t}>{t === "all" ? "All specializations" : t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          type="number"
          placeholder="Min score"
          className="w-28"
          value={range.min}
          onChange={e => setRange(r => ({ ...r, min: e.target.value }))}
          inputMode="numeric"
          data-testid="input-min-score"
        />
        <Input
          type="number"
          placeholder="Max score"
          className="w-28"
          value={range.max}
          onChange={e => setRange(r => ({ ...r, max: e.target.value }))}
          inputMode="numeric"
          data-testid="input-max-score"
        />
        <Button variant="outline" onClick={load} disabled={busy} data-testid="btn-apply-score-filter">Apply</Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="data-table min-w-[880px]">
          <thead>
            <tr>
              <th className="min-w-[200px]">Tester</th>
              <SortableTh label="Score" k="score" sort={sort} onSort={onSort} />
              <th>Tier</th>
              <SortableTh label="Completed" k="completed_count" sort={sort} onSort={onSort} />
              <SortableTh label="Trend" k="trend_delta" sort={sort} onSort={onSort} />
              <SortableTh label="Last active" k="last_assignment_date" sort={sort} onSort={onSort} />
              <th>Flags</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} className="cursor-pointer" onClick={() => nav(`/users/${r.id}`)} data-testid={`row-tester-${r.id}`}>
                <td className="whitespace-nowrap">
                  <b>{r.name}</b>
                  <div className="text-xs text-slate-500">{r.email}</div>
                </td>
                <td className="whitespace-nowrap">
                  <span className="font-display font-bold text-slate-900" data-testid={`score-${r.id}`}>
                    {r.score == null ? "—" : Math.round(r.score)}
                  </span>
                </td>
                <td className="whitespace-nowrap"><TierBadge tier={r.tier} state={r.state} /></td>
                <td className="whitespace-nowrap">{r.completed_count ?? 0}</td>
                <td className="whitespace-nowrap"><TrendCell delta={r.trend_delta} /></td>
                <td className="text-xs text-slate-600 whitespace-nowrap">{r.last_assignment_date ? new Date(r.last_assignment_date).toLocaleDateString() : "—"}</td>
                <td className="whitespace-nowrap">
                  {(r.anomalies || []).length === 0 ? (
                    <span className="text-slate-300 text-xs">—</span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-amber-700 text-xs font-medium"
                      title={r.anomalies.join(" · ")}
                      data-testid={`flag-${r.id}`}
                    >
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {r.anomalies.length} flag{r.anomalies.length > 1 ? "s" : ""}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-slate-500">No testers match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
